import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, LinkIcon, UnlinkIcon, ZapIcon, CopyIcon, HistoryIcon, SendIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
interface RaastRelinkDelinkProps {
  onBack: () => void;
  onHome: () => void;
  // Added to support the shortcut grid (Merchant History, Payment Requests, etc.)
  // Wire this the same way RaastHome's onNavigate is wired.
  onNavigate?: (view: string) => void;
}
type Step = 'list' | 'review' | 'otp' | 'success';
type ActionType = 'link' | 'delink';
// Shared primary button style: height 55px, width 280px, radius 8px, orange fill
const PRIMARY_BUTTON_CLASS = 'h-[55px] w-[280px] mx-auto rounded-lg font-bold text-[16px] bg-[#F37024] text-white shadow-md flex items-center justify-center disabled:opacity-50';
export function RaastRelinkDelink({
  onBack,
  onHome,
  onNavigate
}: RaastRelinkDelinkProps) {
  const {
    bgStyle,
    cardBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('list');
  const [direction, setDirection] = useState(1);
  const [actionType, setActionType] = useState<ActionType>('link');
  const [selectedId, setSelectedId] = useState<any>(null);
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  // Single active account — matches the reference screenshot (one RAAST ID shown, not a list)
  const activeId = {
    id: '03001234567',
    status: 'linked',
    account: PAY_FROM_ACCOUNTS[0]
  };
  const shortcuts = [{
    id: 'history',
    title: 'Merchant History',
    desc: 'View your RAAST merchant payments',
    icon: HistoryIcon,
    view: 'history'
  }, {
    id: 'requests',
    title: 'Payment Requests',
    desc: 'View and manage RTP requests',
    icon: CheckCircle2Icon,
    view: 'requests'
  }, {
    id: 'pay_merchant',
    title: 'Pay to Merchant',
    desc: 'Scan a QR or enter a till number',
    icon: SendIcon,
    view: 'qr_till'
  }, {
    id: 'pay_person',
    title: 'Pay to Person',
    desc: 'Scan a QR or enter a till number',
    icon: SendIcon,
    view: 'pay_person'
  }];
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`relink-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`relink-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  // Header — matches the reference screenshot: "Raast" title (not "Relink / Delink"),
  // icon-only orange home button.
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2696" data-mp="1xikxl7n1ah9e">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2697" data-mp="2bj5ic9cien5j">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2698" data-mp="26i7wnmsajaza" />
      </button>
      <h1 className={`text-[16px] font-normal flex-1 ml-3 ${textPri}`} data-id="element-2699" data-mp="h5mnj79v5inx">
        {title}
      </h1>
      <button onClick={onHome} className="w-10 h-10 flex items-center justify-center" data-id="element-2700" data-mp="c84b36wn0lb6">
        <HomeIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-2701" data-mp="en10hmen5cmr" />
      </button>
    </div>;
  const renderList = () => <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2702" data-mp="nlw892xc2us6">
      {renderHeader('Raast', onBack)}
      <div className="px-6 flex-1 flex flex-col gap-4 pb-8" data-id="element-2703" data-mp="3r46awiyvpvht">
        {/* RAAST ID card — same structure as the Home screen's card */}
        <div className={`rounded-lg p-5 border shadow-sm ${cardBg} ${borderClr}`} data-id="element-2704" data-mp="4vev46ozi7rx">
          <div className="flex items-start justify-between mb-4" data-id="element-2705" data-mp="9lli623ulk82">
            <div className="flex items-center gap-3" data-id="element-2706" data-mp="9ny934rl34x9">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${isDark || isPlatinum ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2707" data-mp="38au18wu8rx7s">
                <ZapIcon className="w-6 h-6 text-[#F37024]" fill="currentColor" data-id="element-2708" data-mp="35rp9wy2al1b2" />
              </div>
              <div data-id="element-2709" data-mp="30q9mizcn6ipi">
                <p className={`text-[11px] font-normal uppercase tracking-wider ${textSec}`} data-id="element-2710" data-mp="2vt9gupjpfhyx">
                  Your RAAST ID
                </p>
                <p className={`text-[18px] font-bold mt-0.5 ${textPri}`} data-id="element-2711" data-mp="2qw9eaom2cxck">
                  0300 1234567
                </p>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#22C55E]/10 text-[#22C55E] flex items-center gap-1" data-id="element-2712" data-mp="2i131h5odlf0j">
              <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-2713" data-mp="2d0nr8mze4nms" />
              Active
            </span>
          </div>

          <div className={`h-px w-full ${borderClr} mb-4`} data-id="element-2714" data-mp="281udquk3swmm" />

          <div className="space-y-3 mb-5" data-id="element-2715" data-mp="2342l1ybmx6hd">
            <div className="flex items-center justify-between" data-id="element-2716" data-mp="1z2z2v5646ycz">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2717" data-mp="1z3seajg0qc1g">
                Account Title
              </span>
              <span className={`text-[13px] font-medium ${textPri}`} data-id="element-2718" data-mp="1ok857lskoa3c">
                KASHISH AHMED
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-2719" data-mp="1fhyyz84c72vr">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2720" data-mp="1kga79eezh5bv">
                Linked Account
              </span>
              <span className={`text-[13px] font-medium ${textPri}`} data-id="element-2721" data-mp="285cnchtyyq49">
                Current Account ••••8910
              </span>
            </div>
            <div className="flex items-center justify-between gap-3" data-id="element-2722" data-mp="2c6oewswellf3">
              <span className={`text-[13px] font-medium ${textSec} shrink-0`} data-id="element-2723" data-mp="1yf06azld5f2v">
                IBAN
              </span>
              <span className={`text-[13px] font-medium truncate ${textPri} flex items-center gap-1.5`} data-id="element-2724" data-mp="23dl64xwpdwqs">
                PK36BOPU0000001234567890
                <CopyIcon className={`w-3.5 h-3.5 ${textSec} shrink-0`} data-id="element-2725" data-mp="2vwibdka2copn" />
              </span>
            </div>
          </div>

          {/* Single Relink/Delink CTA — full width per the screenshot, not the 280px spec */}
          <button onClick={() => {
          setSelectedId(activeId);
          setActionType(activeId.status === 'linked' ? 'delink' : 'link');
          navigateTo('review');
        }} className="w-full h-[55px] rounded-lg bg-[#F37024] text-white font-bold text-[16px] flex items-center justify-center gap-3 shadow-md" data-id="element-2726" data-mp="3185kxlcfepf9">
            <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center" data-id="element-2727" data-mp="2iaupycxtui5m">
              <LinkIcon className="w-4 h-4" data-id="element-2728" data-mp="2walxpvxr32b6" />
            </span>
            Relink/Delink
          </button>
        </div>

        {/* Shortcut grid — 2x2, radius 8px per the tile rule */}
        <div className="grid grid-cols-2 gap-4" data-id="element-2729" data-mp="bik57hybnsb8">
          {shortcuts.map(s => <button key={s.id} onClick={() => onNavigate?.(s.view)} className={`text-left rounded-lg p-4 border ${borderClr} ${isDark ? 'bg-[#2D1810]' : 'bg-[#FDF3ED]'}`} data-id="element-2730" data-mp="6kik6rhc5ix4">
              <s.icon className="w-6 h-6 text-[#F37024] mb-3" strokeWidth={2} data-id="element-2731" data-mp="2533wjtrm4zn5" />
              <p className={`text-[14px] font-medium mb-1 ${textPri}`} data-id="element-2732" data-mp="2a0na0rghl8e1">
                {s.title}
              </p>
              <p className={`text-[11px] font-normal ${textSec}`} data-id="element-2733" data-mp="2esx6yrg0r8pt">{s.desc}</p>
            </button>)}
        </div>
      </div>
    </motion.div>;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2734" data-mp="2opa0npbkdmkk">
      {renderHeader('Confirm Action', () => navigateTo('list', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2735" data-mp="ye5c87vehpdc">
        <div className={`rounded-lg p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2736" data-mp="yfqao8ku5sgh">
          <h3 className={`text-[16px] font-normal mb-6 text-center ${textPri}`} data-id="element-2737" data-mp="13ejndysatffl">
            Confirm {actionType === 'link' ? 'Linking' : 'Delinking'}
          </h3>
          <div className="space-y-4" data-id="element-2738" data-mp="r0wfvvdqwvi6">
            <div className="flex justify-between" data-id="element-2739" data-mp="m3e47x8cg26h">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2740" data-mp="m4625epeybzx">
                RAAST ID
              </span>
              <span className={`text-[14px] font-medium ${textPri}`} data-id="element-2741" data-mp="h64ioso2sanx">
                {selectedId?.id}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2742" data-mp="19t8godor3oq7">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2743" data-mp="19ti6a05vvtvd">
                Action
              </span>
              <span className={`text-[14px] font-medium ${actionType === 'link' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2744" data-mp="14vyud6wnrt6l">
                {actionType === 'link' ? 'Link to Account' : 'Delink from Account'}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('otp')} className={`${PRIMARY_BUTTON_CLASS} mt-6`} data-id="element-2745" data-mp="3fnmujebyaeaj">
          Confirm
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2746" data-mp="3peznk99hjai5">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2747" data-mp="3udu414jzcmz3">
        <div className="mb-8 text-center" data-id="element-2748" data-mp="3uf57125lpqp5">
          <p className={`text-[14px] ${textSec}`} data-id="element-2749" data-mp="c7o4j1xhfqe0">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2750" data-mp="768ip7nhdfuk">
          {otp.map((digit, i) => <input key={i} id={`relink-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-lg text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2751" data-mp="2dz15xh08rg5b" data-mpi={i} />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2752" data-mp="5t8qpgog85uu">
          {timer > 0 ? <p className={`text-[14px] font-normal ${textSec}`} data-id="element-2753" data-mp="artvltenbdpm">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[13px] font-medium text-[#F37024]" data-id="element-2754" data-mp="rjgu4cypy8c">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  // Success — bottom-sheet modal over a dimmed "Review Details / Confirm Action"
  // underlay, matching the reference screenshot.
  const renderSuccess = () => <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className={`w-full min-h-screen flex flex-col relative overflow-hidden ${bgStyle}`} data-id="element-2755" data-mp="2z3odh1rbpsce">
      {/* Dimmed review underlay */}
      <div className="flex-1 flex flex-col pointer-events-none" data-id="element-2756" data-mp="2q5m49yxfrte1">
        {renderHeader('Review Details', () => {})}
        <div className="px-6" data-id="element-2757" data-mp="3is70bcouwtnx">
          <div className={`h-px w-full ${borderClr} mb-5`} data-id="element-2758" data-mp="3iszznslhkgw1" />
          <h3 className={`text-[16px] font-bold text-center mb-6 ${textPri}`} data-id="element-2759" data-mp="3e0yoka020w7l">
            Confirm {actionType === 'link' ? 'Linking' : 'Delinking'}
          </h3>
          <div className="space-y-4" data-id="element-2760" data-mp="391vjwqnzks05">
            <div className="flex justify-between" data-id="element-2761" data-mp="abve7alz3rma">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2762" data-mp="adon6wk26nxg">
                RAAST ID
              </span>
              <span className={`text-[13px] font-bold ${textPri}`} data-id="element-2763" data-mp="5jbzgabjuqfw">
                {selectedId?.id}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2764" data-mp="hv4mf0iqtk9r">
              <span className={`text-[13px] font-medium ${textSec}`} data-id="element-2765" data-mp="hvwfo4ovb5wv">
                Action
              </span>
              <span className={`text-[13px] font-bold ${actionType === 'link' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2766" data-mp="mx3p086nsd5n">
                {actionType === 'link' ? 'Link to Account' : 'Delink from Account'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrim */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="absolute inset-0 bg-black/50 z-20" data-id="element-2767" data-mp="15lqsim96suh4" />

      {/* Bottom sheet */}
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} transition={{
      type: 'spring',
      damping: 28,
      stiffness: 300
    }} className={`absolute bottom-0 left-0 right-0 z-30 rounded-t-[28px] px-6 pt-8 pb-8 flex flex-col items-center ${cardBg}`} data-id="element-2768" data-mp="1enqb3izuo6m6">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 15,
        delay: 0.1
      }} className="w-20 h-20 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-5" data-id="element-2769" data-mp="1focg48ccq10h">
          <div className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center shadow-lg shadow-[#22C55E]/30" data-id="element-2770" data-mp="j06eluaq7oqf">
            <CheckCircle2Icon className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2771" data-mp="rxyt4w8miscr" />
          </div>
        </motion.div>
        <h2 className={`text-[24px] font-bold mb-2 text-center ${textPri}`} data-id="element-2772" data-mp="28euqlufen4nx">
          {actionType === 'link' ? 'Linked Successfully!' : 'Delinked Successfully!'}
        </h2>
        <p className={`text-[14px] text-center mb-8 ${textSec}`} data-id="element-2773" data-mp="3ldefegxrvqfv">
          {actionType === 'link' ? 'Your RAAST ID has been linked to your account.' : 'Your RAAST ID has been delinked from your account.'}
        </p>
        <button onClick={onHome} className="h-[55px] w-[280px] rounded-lg font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2774" data-mp="3h96p4fbo22fj">
          Done
        </button>
      </motion.div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2775" data-mp="cuyolup61lcw">
      {step === 'list' && renderList()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}